import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Boxes, Scissors, Ruler, Truck, Sparkles, ArrowRight, Layers, PaintBucket } from "lucide-react";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/297a8bc7-7df9-44fd-bc49-34e7eb847333/images/849e0f9444a9e12cedc6297265ea789af32a0630a09abf40ea5cd23221c9063f.jpeg";
const SHOW1 =
  "https://images.unsplash.com/photo-1708397016786-8916880649b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxjdXN0b20lMjB3YXJkcm9iZSUyMGludGVyaW9yfGVufDB8fHx8MTc5MDY4MDY0OHww&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: Ruler, title: "Ölçü Girin", desc: "Genişlik, yükseklik ve derinliği milimetrik girin." },
  { icon: PaintBucket, title: "Renk Seçin", desc: "Gövde ve kapak rengini beğeninize göre belirleyin." },
  { icon: Boxes, title: "3D Görün", desc: "Dolabınızı canlı 3D olarak döndürün, kapakları açın." },
  { icon: Scissors, title: "CNC & Fire", desc: "Tabaka üzerindeki kesim planı ve fire oranını görün." },
  { icon: Layers, title: "Maliyet", desc: "Her malzeme için anlık fiyat dökümünü izleyin." },
  { icon: Truck, title: "Teslimat", desc: "Ödeyin, üretelim, kargoyla kapınıza teslim edelim." },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaTarget = user?.role === "admin" ? "/admin" : "/tasarim";

  return (
    <div className="min-h-screen bg-atelier overflow-x-clip">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Milimetrik Hassasiyet • CNC Üretim
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-stone-900">
              Hayalinizdeki Dolabı <span className="text-primary">3D</span> Tasarlayın
            </h1>
            <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-xl">
              Ölçülerinizi girin, gövde ve kapak rengini seçin, raflarınızı belirleyin.
              Dolabınızı canlı 3D olarak görün, kapaklarına tıklayıp açın. CNC kesim planı,
              fire oranı ve maliyet dökümü anında karşınızda.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={ctaTarget}>
                <Button data-testid="hero-cta-button" size="lg" className="rounded-full text-base px-8 h-12">
                  Tasarıma Başla <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              {!user && (
                <Link to="/login">
                  <Button variant="outline" size="lg" className="rounded-full text-base px-8 h-12">
                    Giriş Yap
                  </Button>
                </Link>
              )}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-primary/10 rounded-[2rem] -rotate-2" />
            <img
              src={HERO}
              alt="Taban Mobilya Atölyesi"
              className="relative rounded-[1.75rem] shadow-2xl object-cover w-full h-[420px] lg:h-[520px]"
            />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-[#E6DFD5] hidden sm:block">
              <p className="text-xs font-mono text-stone-500">TABAKA</p>
              <p className="font-serif text-2xl font-bold text-stone-900">2.10 × 2.80 m</p>
              <p className="text-xs text-emerald-600 font-semibold">Optimize kesim • düşük fire</p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mb-2">Nasıl Çalışır?</h2>
        <p className="text-stone-600 mb-10">Altı adımda sipariş — tamamen dijital.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={i} className="card-atelier rounded-2xl p-6">
              <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs text-primary">0{i + 1}</span>
                <h3 className="text-lg font-semibold text-stone-900">{s.title}</h3>
              </div>
              <p className="text-sm text-stone-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Showcase */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-[2rem] overflow-hidden bg-stone-900 grid lg:grid-cols-2">
          <img src={SHOW1} alt="Özel dolap" className="object-cover w-full h-full min-h-[280px]" />
          <div className="p-10 lg:p-14 flex flex-col justify-center">
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-4">
              Atölyeden Kapınıza
            </h2>
            <p className="text-stone-300 leading-relaxed mb-8">
              Siparişiniz üretime alınır, her aşama sistemde takip edilir: hazırlanıyor,
              üretimde, kargoda ve teslim edildi. Her siparişin tarihi ve saati kayıt altındadır.
            </p>
            <Link to={ctaTarget}>
              <Button size="lg" className="rounded-full w-fit px-8">
                Şimdi Tasarla <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E6DFD5] py-8 text-center text-sm text-stone-500">
        © {new Date().getFullYear()} Taban Mobilya Atölyesi — Tüm hakları saklıdır.
      </footer>
    </div>
  );
}
